import { Injectable } from '@angular/core';
import { from, Observable, of } from 'rxjs';
import { map, mergeMap, toArray } from 'rxjs/operators';
import { isNotNil } from '@gelato-api-ui/utils/ramda';
import {
  PriceNavigatorDraftPrices,
  PriceNavigatorItemModel,
  PriceNavigatorListModel,
  PriceNavigatorStoreModel,
  ProductPricesData,
} from '@api-ui-app/src/app/price-navigator/prices/types/price-navigator.types';
import { CurrencyRateService } from '@gelato-api-ui/core/currency-rate/services/currency-rate.service';
import {
  PaginatedProductModel,
  ProductModel,
  VariantModel,
} from '@api-ui-app/src/app/price-navigator/prices/types/price-navigator-product.types';
import { EProductVariantsCollection } from '@gelato-api-ui/core/e-commerce/types/e-product-variants-collection';
import { PRICE_NAVIGATOR_DEFAULT_PAGINATION } from '@api-ui-app/src/app/price-navigator/prices/constants/price-navigator-default-pagination';

@Injectable({
  providedIn: 'root',
})
export class PriceNavigatorProductsMappingService {
  constructor(private readonly currencyRateService: CurrencyRateService) {}

  static calculateProfitBasedOnNewPrices(
    initialPrices: PriceNavigatorItemModel[],
    draftPrices: PriceNavigatorDraftPrices[],
  ): PriceNavigatorItemModel[] {
    return initialPrices.map(product => ({
      ...product,
      variants: product.variants.map(variant => {
        const { retailPrice, shippingCost, productionCost } = variant;
        const draft = draftPrices.find(price => {
          // compare productUids in case of handling draft prices where variantId is not set
          return price.variantId && variant.id
            ? price.variantId === variant.id
            : price.productUid === variant.productUid;
        });

        return {
          ...variant,
          newRetailPrice: draft?.newRetailPrice,
          newCompareAtPrice: draft?.newCompareAtPrice,
          profitChange: isNotNil(draft?.newRetailPrice) ? draft?.newRetailPrice - variant.retailPrice : null,
          estimateProfit: (draft?.newRetailPrice ?? retailPrice) - shippingCost - productionCost,
        };
      }),
    }));
  }

  static getEcommLoadedProductPrices(productPrices: PriceNavigatorItemModel[]): PriceNavigatorItemModel[] {
    return [
      ...productPrices.map((product: PriceNavigatorItemModel) => ({
        ...product,
        variants: [
          ...product.variants.map(variant =>
            variant.pendingPrice ? { ...variant, retailPrice: variant.pendingPrice } : variant,
          ),
        ],
      })),
    ];
  }

  getEcommProductsListData({ products, stores }: PriceNavigatorListModel): ProductPricesData {
    return {
      stores,
      productPrices: this.getDataProducts(products, stores),
      productsTotalCount: products.total,
    };
  }

  // In the situation when we are fetching published + unpublished product list, we need to merge them in on PN table structure using this method
  // variantsCollection is used to populate unpublished variants with product name and title
  mergeEcommProductsListWithUnpublishedListData(
    publishedList: PriceNavigatorListModel,
    unpublishedList: PriceNavigatorListModel,
    variantsCollection: EProductVariantsCollection,
  ): ProductPricesData {
    const stores = [...publishedList.stores];
    const publishedPrices = this.getEcommProductsListData({
      products: publishedList.ecommProducts,
      stores,
    });
    const unpublishedPrices = this.getEcommProductsListData({
      products: unpublishedList.unpublishedProducts,
      stores,
    });

    return {
      ...publishedPrices,
      productPrices: [
        ...publishedPrices.productPrices.map(product => {
          const unpublishedProduct = unpublishedPrices.productPrices.find(({ id }) => id === product.id);

          if (!unpublishedProduct) {
            return product;
          }

          // populate unpublished variants with product name and title
          const unpublishedVariants: VariantModel[] = unpublishedProduct.variants.map(variant => ({
            ...variant,
            title: variantsCollection[variant.productUid].title,
            productName: variantsCollection[variant.productUid].productName,
          }));

          return {
            ...product,
            variants: [...product.variants, ...unpublishedVariants],
          };
        }),
      ],
    };
  }

  getPriceNavigatorListData({ products, stores }: PriceNavigatorListModel): Observable<ProductPricesData> {
    const productPrices = this.getDataProducts(products, stores);
    return of(productPrices).pipe(
      map(productPrices => ({
        stores,
        productPrices,
        productsTotalCount: products.total,
        // Do not start tracking the preloading process if less than 20 products are on the first page.
        preloadingTaskId: productPrices?.length < PRICE_NAVIGATOR_DEFAULT_PAGINATION.limit ? null : products.taskId,
      })),
    );
  }

  calculateVariantsEstimateProfit(products: PriceNavigatorItemModel[]): PriceNavigatorItemModel[] {
    return products.map(product => ({
      ...product,
      variants: product.variants.map(variant => {
        const { retailPrice, shippingCost, productionCost } = variant;
        return { ...variant, estimateProfit: retailPrice - shippingCost - productionCost };
      }),
    }));
  }

  getInitialDraftPricesFromResponse(
    products: PriceNavigatorItemModel[],
    userCurrency: string,
  ): Observable<PriceNavigatorDraftPrices[]> {
    const draftPrices: PriceNavigatorDraftPrices[] = [];

    products?.forEach(productModel => {
      productModel.variants?.forEach(variantModel => {
        if (isNotNil(variantModel.pendingPrice) || isNotNil(variantModel.pendingCompareAtPrice)) {
          draftPrices.push({
            ...this.getDefaultDraftPriceFromProductVariantModel(productModel, variantModel),
            newRetailPrice: variantModel.pendingPrice,
            newCompareAtPrice: variantModel.pendingCompareAtPrice,
            selectedCurrency: userCurrency,
          });
        }
      });
    });

    return this.convertDraftPricesToStoreCurrency(draftPrices);
  }

  getInitialEcommProductDraftPrices(
    products: PriceNavigatorItemModel[],
    userCurrency: string,
    rate: number,
  ): PriceNavigatorDraftPrices[] {
    const draftPrices: PriceNavigatorDraftPrices[] = [];

    products?.forEach(productModel => {
      productModel.variants?.forEach(variantModel => {
        draftPrices.push({
          ...this.getDefaultDraftPriceFromProductVariantModel(productModel, variantModel),
          newRetailPrice: variantModel.pendingPrice ? variantModel.pendingPrice * rate : variantModel.retailPrice,
          newRetailPriceInStoreCurrency: variantModel.pendingPrice
            ? variantModel.pendingPrice
            : variantModel.retailPrice / rate,
          selectedCurrency: userCurrency,
        });
      });
    });

    return draftPrices;
  }

  getCurrencyRateService(products: PriceNavigatorItemModel[], selectedCurrency: string): Observable<number> {
    return this.currencyRateService.get(products[0].store.currency, selectedCurrency);
  }

  private getDefaultDraftPriceFromProductVariantModel(
    productModel: PriceNavigatorItemModel,
    variantModel: VariantModel,
  ): PriceNavigatorDraftPrices {
    return {
      storeId: productModel.storeId,
      storeCurrency: productModel.store.currency,
      newRetailPrice: 0,
      selectedCurrency: '',
      newRetailPriceInStoreCurrency: null,
      newCompareAtPriceInStoreCurrency: null,
      productUid: variantModel.productUid,
      variantId: variantModel.id,
      productId: productModel.id,
      retailPrice: variantModel.retailPrice,
      estimateProfit: variantModel.estimateProfit,
      profitChange: variantModel.profitChange,
    };
  }

  private getDataProducts(
    products: PaginatedProductModel,
    stores: PriceNavigatorStoreModel[],
  ): PriceNavigatorItemModel[] {
    return products.results.map(product => ({
      ...this.calculateProductProfit(product),
      store: stores.find(store => store.storeId === product.storeId),
    }));
  }

  private calculateProductProfit(product: ProductModel): ProductModel {
    const variants = product.variants.map(variant => {
      let fields;

      if (variant.pendingPrice) {
        const newRetailPrice = variant.pendingPrice;

        fields = {
          newRetailPrice,
          estimateProfit: newRetailPrice - variant.shippingCost - variant.productionCost,
          profitChange: newRetailPrice - variant.retailPrice,
        };
      }

      if (isNotNil(variant.pendingCompareAtPrice)) {
        fields = { ...fields, newCompareAtPrice: variant.pendingCompareAtPrice };
      }

      return { ...variant, ...fields };
    });

    return { ...product, variants };
  }

  convertDraftPricesToStoreCurrency(draftPrices: PriceNavigatorDraftPrices[]): Observable<PriceNavigatorDraftPrices[]> {
    return from(draftPrices).pipe(
      mergeMap(draft =>
        this.currencyRateService.get(draft.selectedCurrency, draft.storeCurrency).pipe(
          map(rate => ({
            ...draft,
            newRetailPriceInStoreCurrency: draft.newRetailPrice ? draft.newRetailPrice * rate : null,
            // compareAtPrice can equal 0, so that is we need to check if not null or undefined.
            newCompareAtPriceInStoreCurrency: isNotNil(draft.newCompareAtPrice) ? draft.newCompareAtPrice * rate : null,
          })),
        ),
      ),
      toArray(),
    );
  }
}
